// src/services/zoom-service.js
// Frontend service for Zoom OAuth — connect, disconnect, get status.
// Mirrors the pattern used by calendar-service.js for Google Calendar.

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase-config';

const FUNCTIONS_BASE = 'https://us-west1-divine-frameworks-6cb16.cloudfunctions.net';

/**
 * Initiate Zoom OAuth — redirects the browser to Zoom's consent screen.
 * @param {string} uid — practitioner UID
 */
export const initiateZoomConnect = async (uid) => {
  const url = `${FUNCTIONS_BASE}/initiateZoomAuth?uid=${encodeURIComponent(uid)}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  if (data.url) {
    window.location.href = data.url;
  } else {
    throw new Error(data.error || 'Failed to get Zoom OAuth URL');
  }
};

/**
 * Disconnect Zoom — removes tokens from Firestore and clears connection status.
 * @param {string} uid — practitioner UID
 */
export const disconnectZoom = async (uid) => {
  const response = await fetch(`${FUNCTIONS_BASE}/disconnectZoom`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uid }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to disconnect Zoom');
  }
  return response.json();
};

/**
 * Read Zoom connection status from the practitioner's main Firestore document.
 * @param {string} uid — practitioner UID
 * @returns {Promise<{ connected: boolean, connectedAt: Date|null } | null>}
 */
export const getZoomStatus = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const data = docSnap.data();
    if (!data.zoomConnected) return null;
    return {
      connected: true,
      connectedAt: data.zoomConnectedAt?.toDate?.() || null,
      recordingPreference: data.zoomSettings?.recordingPreference || null,
    };
  } catch (err) {
    console.warn('[Zoom] Error reading status:', err.message);
    return null;
  }
};

/**
 * Save the practitioner's recording preference to Firestore.
 * @param {string} uid — practitioner UID
 * @param {'auto'|'ask'|'none'} preference
 */
export const saveRecordingPreference = async (uid, preference) => {
  const docRef = doc(db, 'practitioners', uid);
  await setDoc(docRef, {
    zoomSettings: {
      recordingPreference: preference,
      recordingPreferenceSetAt: serverTimestamp(),
    },
  }, { merge: true });
};
